// The two failures a request can end in. Everything the session layer and
// the synchronisation engine decide is decided on these.

/** The API answered with an error status. `code` is what the API puts in the body. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly detail: string;
  /** Milliseconds the API asked us to wait, from `Retry-After` on a 429. */
  readonly retryAfterMs: number | null;
  /** INC-11c-2: how many, when a refusal says it beside its code (the active
   *  sites of a client that is not deactivated). */
  readonly count: number | null;
  /** INC-14a-3d-2b: the reports a refusal of a batch names beside its code. */
  readonly ids: readonly string[] | null;

  constructor(
    status: number,
    code: string,
    detail: string,
    retryAfterMs: number | null = null,
    count: number | null = null,
    ids: readonly string[] | null = null,
  ) {
    super(`${status} ${code}: ${detail}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.detail = detail;
    this.retryAfterMs = retryAfterMs;
    this.count = count;
    this.ids = ids;
  }
}

/** The request never reached an answer: no network, a dropped connection, a timeout. */
export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super('network error');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

// Session codes the application distinguishes (contract table of INC-04a).
export const CODES = {
  invalidCredentials: 'invalid_credentials',
  tokenMissing: 'token_missing',
  tokenInvalid: 'token_invalid',
  tokenStale: 'token_stale',
  organizationSuspended: 'organization_suspended',
  refreshInvalid: 'refresh_invalid',
  refreshExpired: 'refresh_expired',
  deviceRevoked: 'device_revoked',
  passwordChangeRequired: 'password_change_required',
  passwordRejected: 'password_rejected',
  permissionDenied: 'permission_denied',
  rateLimited: 'rate_limited',
  tenantNotFound: 'tenant_not_found',
  // INC-07c-1 (D-258): a person's link to choose a password, refused three
  // ways, each told apart so the screen says what happened.
  invitationUnknown: 'invitation_unknown',
  invitationExpired: 'invitation_expired',
  invitationConsumed: 'invitation_consumed',
  // INC-13 (CA-PLT-05): a support link, refused three ways; and every write of
  // the support session it opens.
  supportAccessUnknown: 'support_access_unknown',
  supportAccessExpired: 'support_access_expired',
  supportAccessConsumed: 'support_access_consumed',
  supportReadOnly: 'support_read_only',
} as const;

/** The refusals of a person's link: they say something about the link and
 *  nothing about a session already on the device. */
export const INVITATION_REFUSALS: ReadonlySet<string> = new Set([
  CODES.invitationUnknown,
  CODES.invitationExpired,
  CODES.invitationConsumed,
]);

/** The refusals of a support link: they say something about the link and
 *  nothing about a session already on the device (INC-13). */
export const SUPPORT_REFUSALS: ReadonlySet<string> = new Set([
  CODES.supportAccessUnknown,
  CODES.supportAccessExpired,
  CODES.supportAccessConsumed,
]);

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function isNetworkError(error: unknown): error is NetworkError {
  return error instanceof NetworkError;
}
